import { RepositoryFactory } from './../repositories';
import { useAuthStore } from '@/stores/auth';
import { pinia } from '@/stores/pinia';
import { ModuleRole } from '@/common/rbac';
const merchantRepository = RepositoryFactory.get('merchant');

let userIdToNameMap = {};
let isUserIdNameMapUpdating = false;
// The callers below re-attempt the lookup whenever the map is still empty, which
// for a user who cannot call the endpoint means one failed request per row. This
// flag records "we already resolved as far as we can" so the retry stops.
let isUserIdNameMapResolved = false;

export const getUserMaps = () => userIdToNameMap;

export const replaceIdsWithNamesInDataLegacy = async (data) => {
	if (!data) return data;

	if (
		Object.keys(userIdToNameMap).length === 0 &&
		!isUserIdNameMapUpdating &&
		!isUserIdNameMapResolved
	) {
		await updateUserIdNameMap();
	}

	for (const item of data) {
		await replaceIdWithNameLegacy(item);
	}

	return data;
};

export const replaceIdWithNameLegacy = async (item) => {
	// only dashboard use this for now
	if (!item) return item;

	const keysToUpdate = [
		'created_by',
		'modified_by',
		'deleted_by',
		'refreshed_by',
	];
	for (const key of keysToUpdate) {
		if (item[key] !== null && item[key]) {
			item[key] = userIdToNameMap[item[key]] || item[key];
		}
	}
	item.groupedDocument = true;

	return item;
};

export const replaceIdsWithNamesInData = async (data, customKey) => {
	if (!data) return data;

	if (
		Object.keys(userIdToNameMap).length === 0 &&
		!isUserIdNameMapUpdating &&
		!isUserIdNameMapResolved
	) {
		await updateUserIdNameMap();
	}

	for (const item of data) {
		await replaceIdWithName(item, customKey);
	}

	return data;
};

export const replaceIdWithName = async (item, customKey = 'actorId') => {
	if (!item) return item;

	if (
		Object.keys(userIdToNameMap).length === 0 &&
		!isUserIdNameMapUpdating &&
		!isUserIdNameMapResolved
	) {
		await updateUserIdNameMap();
	}

	const keysToUpdate = ['createdBy', 'modifiedBy', 'deletedBy', 'refreshedBy'];
	for (const key of keysToUpdate) {
		if (item[key] !== null && item[key]) {
			item[key][customKey] =
				userIdToNameMap[item[key][customKey]] || item[key][customKey];
		}
	}
	item.groupedDocument = true;

	return item;
};

export const updateUserIdNameMap = async () => {
	if (isUserIdNameMapUpdating) {
		while (isUserIdNameMapUpdating) {
			await new Promise((resolve) => setTimeout(resolve, 500));
		}
		return;
	}

	// GET /merchant/find/details is platform:user (thanos-api merchant.go:31).
	// Without it the ids simply stay unresolved and are rendered raw.
	if (!useAuthStore(pinia).can(ModuleRole.PlatformUser)) {
		isUserIdNameMapResolved = true;
		return;
	}

	try {
		isUserIdNameMapUpdating = true;

		const response = await merchantRepository.details();
		const merchants = response?.data?.data;

		Object.keys(merchants).forEach((m) => {
			const { id, name } = merchants[m];
			userIdToNameMap[id] = name;
			merchants[m].keys.forEach((u) => {
				userIdToNameMap[u.id] = u.name;
			});
		});
		isUserIdNameMapResolved = true;
	} catch (error) {
		console.error('Error fetching user/api names:', error);
	} finally {
		isUserIdNameMapUpdating = false;
	}
};

export const cleanUserIdCache = () => {
	userIdToNameMap = {};
	isUserIdNameMapResolved = false;
};
